import { hintTexts } from './hintsConsts';

export type HintCondition =
    | { type: 'page-enter' }
    | { type: 'element-present'; selector: string }
    | { type: 'hint-done'; hintId: string }
    | { type: 'hint-not-shown'; hintId: string }
    | { type: 'time-on-page'; minutes: number }
    | { type: 'time-after-hint-done'; hintId: string; minutes: number };

export type HintBehavior =
    | { type: 'action'; label?: string; actionSelector?: string; permanent?: boolean }
    | { type: 'next'; label: string; nextHintId: string; actionSelector?: string }
    | { type: 'ok'; label: string };

export type BackdropOptions = {
    pad?: number; // внутренний отступ вокруг элемента, по умолчанию 3
    outerPad?: number; // внешняя незатемнённая зона, по умолчанию 10
    r?: number; // радиус скругления, по умолчанию 8
    offsetX?: number; // смещение окна по горизонтали, по умолчанию 0
    offsetY?: number; // смещение окна по вертикали, по умолчанию 0
    extraWidth?: number; // изменение ширины окна (>0 шире, <0 уже), по умолчанию 0
    extraHeight?: number; // изменение высоты окна (>0 выше, <0 ниже), по умолчанию 0
};

export type HintConfig = {
    id: string;
    pagePattern: RegExp;
    condition: HintCondition;
    targetSelector: string;
    tooltipPosition: 'top' | 'right' | 'bottom' | 'left';
    text: string;
    behavior: HintBehavior;
    maxShows?: number;
    scrollTo?: boolean;
    prepareSelector?: string;
    backdropOptions?: BackdropOptions;
};

export const hintsConfig: HintConfig[] = [
    {
        id: 'invite-colleague',
        pagePattern: /^\/home$/,
        condition: {
            type: 'element-present',
            selector: '[data-hint-target="my-team-empty"]',
        },
        targetSelector: '[data-react-root="renderMyTeam"]',
        tooltipPosition: 'left',
        text: hintTexts.HINT_TEXT_INVITE_COLLEAGUE,
        behavior: {
            type: 'action',
            actionSelector: '[data-hint-target="invite-colleague-btn"]',
        },
        backdropOptions: {
            offsetX: 4,
            extraWidth: -4,
            extraHeight: -4,
        },
        maxShows: 5,
    },
    {
        id: 'add-project',
        pagePattern: /^\/home$/,
        condition: {
            type: 'element-present',
            selector: '[data-hint-target="my-team-has-employees"]',
        },
        targetSelector: '.add-project-btn',
        tooltipPosition: 'top',
        text: hintTexts.HINT_TEXT_ADD_PROJECT,
        behavior: {
            type: 'action',
            actionSelector: '.add-project-btn',
        },
        backdropOptions: {
            offsetX: 2,
            offsetY: -2,
            extraWidth: -8,
            extraHeight: -8,
        },
    },
    {
        id: 'dashboard-info-1',
        pagePattern: /\/dashboard$/,
        condition: { type: 'page-enter' },
        targetSelector: '#project_information',
        tooltipPosition: 'right',
        text: hintTexts.HINT_TEXT_DASHBOARD_INFO_1,
        behavior: {
            type: 'next',
            label: hintTexts.HINT_TEXT_BEHAVIOUR_TYPE_NEXT_LABEL,
            nextHintId: 'dashboard-info-2',
        },
        backdropOptions: {
            offsetX: 2,
            offsetY: -2,
            extraWidth: -8,
            extraHeight: -8,
        },
    },
    {
        id: 'dashboard-info-2',
        pagePattern: /\/dashboard$/,
        condition: { type: 'hint-done', hintId: 'dashboard-info-1' },
        targetSelector: '[data-hint-target="dashboard-info-2-target"]',
        tooltipPosition: 'bottom',
        text: hintTexts.HINT_TEXT_DASHBOARD_INFO_2,
        behavior: {
            type: 'next',
            label: hintTexts.HINT_TEXT_BEHAVIOUR_TYPE_NEXT_LABEL,
            nextHintId: 'dashboard-info-3',
        },
        backdropOptions: {
            offsetX: 2,
            offsetY: -2,
            extraWidth: -8,
            extraHeight: -8,
        },
    },
    {
        id: 'dashboard-info-3',
        pagePattern: /\/dashboard$/,
        condition: { type: 'hint-done', hintId: 'dashboard-info-2' },
        targetSelector: '#project_documents_and_finance',
        tooltipPosition: 'right',
        text: hintTexts.HINT_TEXT_DASHBOARD_INFO_3,
        scrollTo: true,
        behavior: {
            type: 'next',
            label: hintTexts.HINT_TEXT_BEHAVIOUR_TYPE_NEXT_LABEL,
            nextHintId: 'dashboard-info-4',
        },
        backdropOptions: {
            offsetX: 2,
            offsetY: -2,
            extraWidth: -8,
            extraHeight: -8,
        },
    },
    {
        id: 'dashboard-info-4',
        pagePattern: /\/dashboard$/,
        condition: { type: 'hint-done', hintId: 'dashboard-info-3' },
        targetSelector: '[data-hint-target="first-draft-estimate"]',
        tooltipPosition: 'top',
        text: hintTexts.HINT_TEXT_DASHBOARD_INFO_4,
        scrollTo: true,
        prepareSelector: '[data-tab-value="inDraft"]',
        behavior: {
            type: 'action',
            actionSelector: '[data-hint-target="first-draft-estimate"]',
            permanent: true,
        },
        backdropOptions: {
            offsetX: 2,
            offsetY: -2,
            extraWidth: 4,
            extraHeight: 4,
        },
    },
    {
        id: 'estimate-info-1',
        pagePattern: /^\/estimate\//,
        condition: { type: 'page-enter' },
        targetSelector: '[data-hint-target="estimate-tabs"]',
        tooltipPosition: 'bottom',
        text: hintTexts.HINT_TEXT_ESTIMATE_INFO_1,
        behavior: {
            type: 'next',
            label: hintTexts.HINT_TEXT_BEHAVIOUR_TYPE_NEXT_LABEL,
            nextHintId: 'estimate-info-2',
        },
        backdropOptions: {
            offsetX: 2,
            offsetY: -2,
            extraWidth: -8,
            extraHeight: 0,
        },
    },
    {
        id: 'estimate-info-2',
        pagePattern: /^\/estimate\//,
        condition: { type: 'hint-done', hintId: 'estimate-info-1' },
        targetSelector: '[data-hint-target="estimate-role-tabs"]',
        tooltipPosition: 'bottom',
        text: hintTexts.HINT_TEXT_ESTIMATE_INFO_2,
        behavior: {
            type: 'next',
            label: hintTexts.HINT_TEXT_BEHAVIOUR_TYPE_NEXT_LABEL,
            nextHintId: 'estimate-info-3',
        },
        backdropOptions: {
            offsetX: 2,
            offsetY: -2,
            extraWidth: -8,
            extraHeight: 0,
        },
    },
    {
        id: 'estimate-info-3',
        pagePattern: /^\/estimate\//,
        condition: { type: 'hint-done', hintId: 'estimate-info-2' },
        targetSelector: '#estimate-managers',
        tooltipPosition: 'right',
        text: hintTexts.HINT_TEXT_ESTIMATE_INFO_3,
        behavior: {
            type: 'ok',
            label: hintTexts.HINT_TEXT_BEHAVIOUR_OK_LABEL,
        },
        backdropOptions: {
            offsetX: 2,
            offsetY: -2,
            extraWidth: -8,
            extraHeight: 4,
        },
    },
    {
        id: 'estimate-info-4',
        pagePattern: /^\/estimate\//,
        condition: { type: 'time-after-hint-done', hintId: 'estimate-info-3', minutes: 5 },
        targetSelector: '[data-hint-target="estimate-customer-tab"]',
        tooltipPosition: 'bottom',
        text: hintTexts.HINT_TEXT_ESTIMATE_INFO_4,
        behavior: {
            type: 'next',
            label: hintTexts.HINT_TEXT_BEHAVIOUR_TYPE_NEXT_LABEL,
            nextHintId: 'estimate-info-5',
        },
        backdropOptions: {
            offsetX: 2,
            offsetY: -2,
            extraWidth: -8,
            extraHeight: 0,
        },
    },
    {
        id: 'estimate-info-5',
        pagePattern: /^\/estimate\//,
        condition: { type: 'hint-done', hintId: 'estimate-info-4' },
        targetSelector: '[data-hint-target="estimate-status"]',
        tooltipPosition: 'right',
        text: hintTexts.HINT_TEXT_ESTIMATE_INFO_5,
        behavior: {
            type: 'ok',
            label: hintTexts.HINT_TEXT_BEHAVIOUR_OK_LABEL,
        },
        backdropOptions: {
            offsetX: 2,
            offsetY: -2,
            extraWidth: 0,
            extraHeight: 2,
        },
    },
    {
        id: 'schedule-info-1',
        pagePattern: /\/schedule$/,
        condition: {
            type: 'element-present',
            selector: '[data-hint-target="schedule-draft-marker"]',
        },
        targetSelector: '[data-hint-target="schedule-first-stage"]',
        tooltipPosition: 'bottom',
        text: hintTexts.HINT_TEXT_SCHEDULE_INFO_1,
        behavior: { type: 'ok', label: hintTexts.HINT_TEXT_BEHAVIOUR_OK_LABEL },
        backdropOptions: {
            offsetX: 2,
            offsetY: -2,
            extraWidth: -16,
            extraHeight: -16,
        },
    },
    {
        id: 'schedule-info-2',
        pagePattern: /\/schedule$/,
        condition: { type: 'hint-done', hintId: 'schedule-info-1' },
        targetSelector: '[data-hint-target="schedule-expand-all"]',
        tooltipPosition: 'bottom',
        text: hintTexts.HINT_TEXT_SCHEDULE_INFO_2,
        behavior: {
            type: 'next',
            label: hintTexts.HINT_TEXT_BEHAVIOUR_EXPAND_ALL_LABEL,
            nextHintId: 'schedule-info-3',
            actionSelector: '[data-hint-target="schedule-expand-all"]',
        },
        backdropOptions: {
            offsetX: 6,
            offsetY: -2,
            extraWidth: -4,
            extraHeight: -2,
        },
    },
    {
        id: 'schedule-info-3',
        pagePattern: /\/schedule$/,
        condition: { type: 'hint-done', hintId: 'schedule-info-2' },
        targetSelector: '[data-hint-target="schedule-plan-purchases"]',
        tooltipPosition: 'bottom',
        text: hintTexts.HINT_TEXT_SCHEDULE_INFO_3,
        behavior: {
            type: 'action',
            actionSelector: '[data-hint-target="schedule-plan-purchases"]',
            permanent: true,
        },
        backdropOptions: {
            offsetX: 6,
            offsetY: -2,
            extraWidth: -4,
            extraHeight: -2,
        },
    },
];
